// THE MERGE AND THE RESTORE's data layer (Module 09, P3-E2 PR E2-2b; spec 09
// §3.13, §3.14, §4.2's E2-2b annotation, §4.17 and §5's E2-2 rows 15 to 17;
// D-324 rulings 4 to 16).
//
// THE PREVIEW IS A QUERY THAT POSTS. `POST /v1/customers/{id}/merge-preview`
// writes nothing (§5's E2-2 row 15), so it is read here as a query keyed on the
// pair, which gives the screen a loading, an error and a refetch for free. It
// sends no `Idempotency-Key`: the route is exempt and stores no answer (D-334
// ruling 4, as the duplicate check is under D-333 ruling 1), because the
// answer names both customers' legal names and tax ids and the idempotency
// store never deletes a row. Every read is therefore a fresh read, which is
// what `MERGE_STALE`'s remedy needs: reading the preview AGAIN (§4.17) must
// hand the admin the new versions, never a replay of the stale ones.
//
// THE PREVIEW NEVER REFRESHES BEHIND THE ADMIN. No refetch on focus, on
// reconnect or on an interval, and nothing refetches it but the screen's own
// `MERGE_STALE` handling (its key sits outside the `sales` branch, so no sales
// write's invalidation reaches it either). The merge sends back the versions and the access set
// the preview read, and the admin's ticks and field choices were made against
// the values on screen. A silent refresh would swap those values and versions
// under a Confirm the admin had already decided on, which is exactly the change
// `MERGE_STALE` exists to stop and to SAY (§4.17's "says what changed").
//
// THE MERGE TAKES ITS KEY FROM THE CALLER. One Confirm press is one key, and
// the screen's single automatic retry after a 503 `RESOURCE_BUSY` resends the
// same key with the same body: a 5xx releases the key (the idempotency layer
// stores no 5xx, `apps/api/src/platform/idempotency.interceptor.ts`), so the
// retry is the same request made again, never a second merge. Every refusal
// below 500 is stored with its key, so a new press after a refusal mints a
// new key with its new body.
//
// AFTER A MERGE EVERY SALES READ IS STALE. The merge moves the loser's
// contacts, notes, quotes, orders, deals, tasks, notifications, overrides and
// distributor products, and module 17's conversations with them (§3.13), so
// the whole `sales` branch and the `messaging` branch are invalidated rather
// than a list of keys that would miss the next table E2-2a2 adds. The answer
// nests no `Customer` (§2.26), so the winner's 360 is read again.
//
// THE RESTORE answers the customer (§5's E2-2 row 17); its 360 and the book's
// lists are read again, as the archive's are.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { components } from "@assessly/api-client";
import { client } from "../../../../lib/api";
import { newIdempotencyKey, unwrap } from "../../../../lib/request";
import { salesKeys } from "./sales-queries";

type S = components["schemas"];

export type MergePreview = S["CustomerMergePreview"];
export type MergeProfile = S["CustomerMergeProfile"];
export type MergeRequest = S["CustomerMergeRequest"];
export type MergeResult = S["CustomerMergeResult"];
export type MergeFieldChoices = S["CustomerMergeFieldChoices"];
export type MergeSide = S["CustomerMergeSide"];
export type MergeBlocker = S["CustomerMergeBlocker"];
export type MergeAccessGrant = S["CustomerMergeAccessGrant"];
export type MergeMoveCounts = S["CustomerMergeMoveCounts"];
export type MergeStayCounts = S["CustomerMergeStayCounts"];

export const mergeKeys = {
  /** Deliberately OUTSIDE the `sales` branch. The merge invalidates that whole
   * branch on success, and a preview under it would be posted again for a
   * pair whose loser is now a tombstone, in the instant before the screen
   * leaves for the winner's 360. Nothing else may refresh it either. */
  preview: (winnerId: string, loserId: string) =>
    ["sales-merge-preview", winnerId, loserId] as const,
};

/** The preview of merging `loserId` into `winnerId`, idle until both are
 * known. It sends no `Idempotency-Key` (see the file comment). */
export function useMergePreview(winnerId: string, loserId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: mergeKeys.preview(winnerId, loserId ?? ""),
    enabled: enabled && winnerId.length > 0 && loserId !== undefined && loserId.length > 0,
    queryFn: async ({ signal }): Promise<MergePreview> => {
      const res = await client.POST("/v1/customers/{id}/merge-preview", {
        params: { path: { id: winnerId } },
        body: { loser_id: loserId ?? "" },
        signal,
      });
      return unwrap(res, "Could not read the merge preview");
    },
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

export interface MergeInput {
  readonly winnerId: string;
  readonly body: MergeRequest;
  /** One key per Confirm press, reused only by the 503 retry. */
  readonly idempotencyKey: string;
}

/** `POST /v1/customers/{id}/merge`. Invalidation runs on success only: a
 * refused merge wrote nothing (§3.13). */
export function useMergeCustomers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: MergeInput): Promise<MergeResult> => {
      const res = await client.POST("/v1/customers/{id}/merge", {
        params: {
          path: { id: input.winnerId },
          header: { "Idempotency-Key": input.idempotencyKey },
        },
        body: input.body,
      });
      return unwrap(res, "Could not merge the customers");
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: salesKeys.all });
      void qc.invalidateQueries({ queryKey: ["messaging"] });
    },
  });
}

/** `POST /v1/customers/{id}/restore` (§3.14). A 409 `CUSTOMER_MERGED` means
 * the customer was merged since the 360 was read, so the 360 is read again on
 * that refusal too, and then shows the tombstone with the winner's name. */
export function useRestoreCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await client.POST("/v1/customers/{id}/restore", {
        params: { path: { id }, header: { "Idempotency-Key": newIdempotencyKey() } },
      });
      return unwrap(res, "Could not restore the customer");
    },
    onSettled: (_data, _error, id) => {
      void qc.invalidateQueries({ queryKey: salesKeys.customer(id) });
      void qc.invalidateQueries({ queryKey: salesKeys.customerLists() });
    },
  });
}
